const ABSOLUTE_URL_PATTERN = /^(?:[a-z]+:)?\/\//i;
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);

type LocationLike = Pick<Location, 'protocol' | 'hostname'>;

const normalizeLoopbackHost = (host?: string | null): string =>
  (host ?? '').trim().replace(/^\[(.*)\]$/, '$1').toLowerCase();

const isLoopbackHost = (host?: string | null): boolean => {
  const normalized = normalizeLoopbackHost(host);
  return normalized.length > 0 && LOOPBACK_HOSTS.has(normalized);
};

const browserLocation = (): LocationLike | undefined =>
  typeof window !== 'undefined' && window.location ? window.location : undefined;

const normalizePath = (rawPath: string): string => {
  if (!rawPath) {
    return '';
  }
  return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
};

const splitUrl = (input: string): { path: string; search: string; hash: string } => {
  const hashIndex = input.indexOf('#');
  const hash = hashIndex >= 0 ? input.slice(hashIndex) : '';
  const baseWithoutHash = hashIndex >= 0 ? input.slice(0, hashIndex) : input;

  const queryIndex = baseWithoutHash.indexOf('?');
  const search = queryIndex >= 0 ? baseWithoutHash.slice(queryIndex) : '';
  const path = queryIndex >= 0 ? baseWithoutHash.slice(0, queryIndex) : baseWithoutHash;

  return { path, search, hash };
};

const sanitizeSegments = (value: string): string[] =>
  value
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

const mergeSegments = (baseSegments: string[], endpointSegments: string[]): string[] => {
  if (baseSegments.length === 0) {
    return endpointSegments;
  }

  if (endpointSegments.length === 0) {
    return baseSegments;
  }

  const matchesFullBase =
    baseSegments.length <= endpointSegments.length &&
    baseSegments.every(
      (segment, index) =>
        endpointSegments[index]?.toLowerCase() === segment.toLowerCase(),
    );

  if (matchesFullBase) {
    return baseSegments.concat(endpointSegments.slice(baseSegments.length));
  }

  const firstMatches =
    endpointSegments[0]?.toLowerCase() === baseSegments[0].toLowerCase();

  if (
    firstMatches &&
    baseSegments.length > 1 &&
    endpointSegments.length > 1 &&
    /^v\d+/i.test(endpointSegments[1]) &&
    endpointSegments[1].toLowerCase() !== baseSegments[1].toLowerCase()
  ) {
    return endpointSegments;
  }

  if (firstMatches) {
    return baseSegments.concat(endpointSegments.slice(1));
  }

  return baseSegments.concat(endpointSegments);
};

const composeUrl = (baseRaw: string, endpoint: string): string => {
  if (ABSOLUTE_URL_PATTERN.test(endpoint)) {
    return endpoint;
  }

  const trimmedBase = (baseRaw || '').trim();
  const normalizedBase = trimmedBase.replace(/\/$/, '');
  const { path: rawPath, search, hash } = splitUrl(endpoint);
  const normalizedPath = normalizePath(rawPath);

  if (!normalizedBase) {
    const fallbackPath = normalizedPath || '/';
    return `${fallbackPath}${search}${hash}`;
  }

  const suffixMatch = normalizedBase.match(/\/api(?:\/v\d+)?$/i);
  if (!suffixMatch) {
    const finalPath = normalizedPath || '/';
    return `${normalizedBase}${finalPath}${search}${hash}`;
  }

  const suffix = suffixMatch[0];
  const baseRoot = normalizedBase.slice(0, -suffix.length) || '';
  const baseSegments = sanitizeSegments(suffix);
  const endpointSegments = sanitizeSegments(normalizedPath);
  const mergedSegments = mergeSegments(baseSegments, endpointSegments);
  const combinedPath = mergedSegments.length > 0 ? `/${mergedSegments.join('/')}` : '/';

  if (!baseRoot) {
    return `${combinedPath}${search}${hash}`;
  }

  return `${baseRoot}${combinedPath}${search}${hash}`;
};

export const alignLoopbackBaseToCurrentHost = (
  baseRaw?: string | null,
  currentLocation: LocationLike | undefined = browserLocation(),
): string => {
  const trimmedBase = (baseRaw || '').trim();
  if (!trimmedBase || !ABSOLUTE_URL_PATTERN.test(trimmedBase) || !currentLocation) {
    return trimmedBase;
  }

  try {
    const baseUrl = new URL(trimmedBase);
    const baseHost = normalizeLoopbackHost(baseUrl.hostname);
    const currentHost = normalizeLoopbackHost(currentLocation.hostname);
    if (!isLoopbackHost(baseHost) || !isLoopbackHost(currentHost) || baseHost === currentHost) {
      return trimmedBase;
    }

    baseUrl.hostname = currentHost === '0.0.0.0' ? '127.0.0.1' : currentHost;
    return baseUrl.toString();
  } catch {
    return trimmedBase;
  }
};

export const getConfiguredApiBaseUrl = (): string =>
  (import.meta.env.VITE_API_BASE_URL || '').trim();

export const resolveApiUrl = (endpoint: string, baseOverride?: string): string => {
  const base = alignLoopbackBaseToCurrentHost(baseOverride ?? getConfiguredApiBaseUrl());
  return composeUrl(base, endpoint);
};

export const resolveApiPath = (endpoint: string): string => resolveApiUrl(endpoint);

export const isAbsoluteUrl = (value: string): boolean => ABSOLUTE_URL_PATTERN.test(value);
